/**
 * Error handling utilities for the Solana Staking SDK.
 *
 * @remarks
 * This module provides error types and utilities following the same
 * patterns as the BD-SDK for consistency.
 */
/**
 * Base error class for Solana Staking SDK.
 */
export class SolanaStakingError extends Error {
    code;
    statusCode;
    details;
    originalError;
    constructor(message, options = {}) {
        super(message);
        this.name = "SolanaStakingError";
        this.code = options.code;
        this.statusCode = options.statusCode;
        this.details = options.details;
        this.originalError = options.originalError;
        const anyError = Error;
        if (typeof anyError.captureStackTrace === "function") {
            // @ts-expect-error
            anyError.captureStackTrace(this, SolanaStakingError);
        }
    }
}
/**
 * Error for transaction-related failures.
 */
export class SolanaTransactionError extends SolanaStakingError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "SolanaTransactionError";
    }
}
/**
 * Error for validator-related failures.
 */
export class SolanaValidatorError extends SolanaStakingError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "SolanaValidatorError";
    }
}
/**
 * Error for RPC connection failures.
 */
export class SolanaRpcError extends SolanaStakingError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "SolanaRpcError";
    }
}
/**
 * Type guard to check if an error is a Solana Staking SDK error.
 *
 * @param error - Error to check
 * @returns True if the error is a Solana Staking SDK error
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.solanaStaking.createStakeAccount();
 * } catch (error) {
 *   if (isSolanaStakingError(error)) {
 *     console.log("SDK Error:", error.message, error.code);
 *   } else {
 *     console.log("Unexpected error:", error);
 *   }
 * }
 * ```
 */
export function isSolanaStakingError(error) {
    if (typeof error !== "object" || error === null)
        return false;
    const maybe = error;
    const name = typeof maybe.name === "string" ? maybe.name : undefined;
    const hasMessage = typeof maybe.message === "string";
    const isKnownName = name === "SolanaStakingError" ||
        name === "SolanaTransactionError" ||
        name === "SolanaValidatorError" ||
        name === "SolanaRpcError";
    return hasMessage && (error instanceof SolanaStakingError || isKnownName);
}
//# sourceMappingURL=data:application/json;base64,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